import { useEffect, useState } from 'react';
import {
  useGetLocationQuery,
  useGetVoiceProfilesQuery,
  usePreviewVoiceMutation,
  useUpdateLocationMutation,
  type VoiceSettingsPatch
} from '@/store/configApi';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, Panel, SkeletonRows } from '@/components/ui';

const STYLES = [
  { value: 'WARM', label: 'Warm', example: '"Perfect, I\'ve got three masala dosas."' },
  { value: 'BRIEF', label: 'Brief', example: '"Three masala dosas."' },
  { value: 'FORMAL', label: 'Formal', example: '"Thank you. I have added three masala dosas."' }
] as const;

/**
 * Voice controls, including the two settings that most change how the line
 * feels on a real call: whether callers can talk over it, and how long it waits
 * before deciding they have finished.
 */
export function VoiceSettings({ locationId }: { locationId: string }) {
  const dispatch = useAppDispatch();
  const location = useGetLocationQuery(locationId, { skip: !locationId });
  const profiles = useGetVoiceProfilesQuery();
  const [update, { isLoading: saving }] = useUpdateLocationMutation();
  const [preview, previewState] = usePreviewVoiceMutation();

  const [form, setForm] = useState<VoiceSettingsPatch>({});

  useEffect(() => {
    if (!location.data) return;
    setForm({
      voiceProfileId: location.data.voiceProfileId,
      speakingSpeed: location.data.speakingSpeed ?? 1,
      language: location.data.language ?? 'en-US',
      responseStyle: location.data.responseStyle ?? 'WARM',
      bargeInEnabled: location.data.bargeInEnabled !== false,
      endOfSpeechSilenceMs: location.data.endOfSpeechSilenceMs ?? 900
    });
  }, [location.data]);

  const save = async () => {
    const result = await update({ id: locationId, ...form });
    dispatch(
      'error' in result
        ? pushToast('Could not save your voice settings.', 'error')
        : pushToast('Voice settings saved.', 'success')
    );
  };

  const hear = async () => {
    const result = await preview({
      locationId,
      text: "Thanks for calling. That's three masala dosas and one mango lassi, thirty-seven forty-nine. Would you like anything else?"
    });

    if ('error' in result) {
      dispatch(pushToast('Could not generate a preview.', 'error'));
      return;
    }

    if (result.data.audioUrl) {
      await new Audio(result.data.audioUrl).play().catch(() => {
        dispatch(pushToast('Your browser blocked playback. Try again.', 'error'));
      });
    } else {
      // No TTS provider: there is nothing to play, and saying so is more useful
      // than a silent button.
      dispatch(
        pushToast(
          'No speech provider is configured, so callers hear the carrier voice.',
          'info'
        )
      );
    }
  };

  if (location.isLoading) {
    return (
      <Panel title="Voice">
        <SkeletonRows rows={4} />
      </Panel>
    );
  }

  return (
    <Panel title="How the line sounds">
      <div className="space-y-5 px-4 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="voice" className="block text-small font-medium text-ink">
              Voice
            </label>
            <select
              id="voice"
              value={form.voiceProfileId ?? ''}
              onChange={(event) =>
                setForm({ ...form, voiceProfileId: event.target.value || undefined })
              }
              className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            >
              <option value="">Default</option>
              {profiles.data?.data.map((profile) => (
                <option key={profile._id} value={profile._id}>
                  {profile.name}
                  {profile.accent ? ` (${profile.accent})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="speed" className="block text-small font-medium text-ink">
              Speaking speed
              <span className="tabular ml-2 text-slate">{(form.speakingSpeed ?? 1).toFixed(2)}×</span>
            </label>
            <input
              id="speed"
              type="range"
              min={0.75}
              max={1.25}
              step={0.05}
              value={form.speakingSpeed ?? 1}
              onChange={(event) =>
                setForm({ ...form, speakingSpeed: Number(event.target.value) })
              }
              className="w-full accent-[#0B7A5A]"
            />
          </div>
        </div>

        <fieldset>
          <legend className="text-small font-medium text-ink">Wording</legend>
          <p className="mt-0.5 text-micro text-slate">
            The line varies its phrasing between calls so it does not sound recorded. All
            wording is pre-approved; it never makes anything up.
          </p>
          <div className="mt-2 space-y-2">
            {STYLES.map((style) => (
              <label key={style.value} className="flex items-start gap-2 text-small">
                <input
                  type="radio"
                  name="responseStyle"
                  checked={form.responseStyle === style.value}
                  onChange={() => setForm({ ...form, responseStyle: style.value })}
                  className="mt-1 h-4 w-4 accent-[#0B7A5A]"
                />
                <span>
                  <span className="text-ink">{style.label}</span>
                  <span className="block text-micro text-slate">{style.example}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex items-start gap-2 rounded bg-paper px-3 py-2.5 text-small">
          <input
            type="checkbox"
            checked={form.bargeInEnabled !== false}
            onChange={(event) => setForm({ ...form, bargeInEnabled: event.target.checked })}
            className="mt-0.5 h-4 w-4 accent-[#0B7A5A]"
          />
          <span>
            <span className="font-medium text-ink">Let callers interrupt</span>
            <span className="block text-slate">
              The line stops talking the moment a caller starts. Turn this off only if a noisy
              room is cutting it off by mistake.
            </span>
          </span>
        </label>

        <div className="space-y-1.5">
          <label htmlFor="silence" className="block text-small font-medium text-ink">
            Pause before the line replies
            <span className="tabular ml-2 text-slate">{form.endOfSpeechSilenceMs ?? 900} ms</span>
          </label>
          <input
            id="silence"
            type="range"
            min={400}
            max={2000}
            step={100}
            value={form.endOfSpeechSilenceMs ?? 900}
            onChange={(event) =>
              setForm({ ...form, endOfSpeechSilenceMs: Number(event.target.value) })
            }
            className="w-full accent-[#0B7A5A]"
          />
          <p className="text-micro text-slate">
            Shorter feels quicker but risks cutting people off mid-order. Longer is safer but
            leaves a gap after every sentence.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button loading={saving} onClick={save}>
            Save voice settings
          </Button>
          <Button variant="secondary" loading={previewState.isLoading} onClick={hear}>
            Hear it
          </Button>
          {previewState.data ? (
            <span className="tabular text-micro text-slate">
              {previewState.data.provider} · {previewState.data.latencyMs} ms
            </span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
